// ============================================================
// ValidationSummary.tsx — the case page's required-fields row (option B of
// design/required-card-mockups): "To fill in" + small outline chips in one
// row behind a thin amber left accent — no fill, no headline, no count (the
// hero already says "N fields incomplete").
// Only fields the user can fill in NOW: a locked or hidden section's fields
// are left out entirely (fillableFields), so if everything left is locked
// the row isn't shown at all. Each chip is the field's name; its section is
// in the tooltip (hover and keyboard focus) and the accessible name. A chip
// goes to its section (a navigation — Back returns) and focuses the field
// (fieldFocusRequest: the section's card engine opens the card and focuses
// it). More than FOLD_AT → the first few + "+N more" / "Show fewer".
// When the last one is filled in, the row folds away smoothly (no jump).
// WHICH fields count: requiredFieldsFor (validateCase + "Limitation date" on
// an open case with no limitation-type deadline) — the SAME list the hero's
// "N fields incomplete" counts. A locked section's fields are listed only when
// it's the section the page is open on (openSection).
// ============================================================
import React, { useEffect, useState } from 'react'
import { useCasevoStore } from '../../store/useCasevoStore'
import { requestFieldFocus } from '../../utils/fieldFocusRequest'
import { KIcon } from '../case/sections/sectionKit'
import RailTooltip from './RailTooltip'
import type { ViewingPaneHeading } from '../../types/case.types'
import { FOLD_AT, requiredFieldsFor, visibleFields, type MissingField } from './requiredFields'

// The fold-away's length (ms) — the row unmounts once it has finished. A
// timer, not transitionend: with reduced motion there's no transition at all.
export const FOLD_MS = 300
const CHIP = 'inline-flex items-center gap-[5px] h-[24px] px-[9px] rounded-full border text-[12.5px] whitespace-nowrap transition-colors focus:outline-none casevo-focus'
const CHIP_FIELD = `${CHIP} border-casevo-border bg-casevo-surface text-casevo-forest hover:border-casevo-mid hover:text-casevo-link`
const CHIP_MORE = `${CHIP} border-dashed border-casevo-border bg-transparent text-casevo-text-soft hover:text-casevo-link`

const ValidationSummary: React.FC<{
  // Go to a section (the case page's own section navigation).
  onGo: (section: ViewingPaneHeading) => void
  // Sections that can't be opened yet ("Complete Client details first").
  lockReasonByKey?: Partial<Record<ViewingPaneHeading, string>>
  // Sections shown in the case menu (a hidden one can't be gone to).
  visibleKeys?: string[]
  // The section the page is open on (its fields count even if it's locked).
  openSection?: string | null
}> = ({ onGo, lockReasonByKey = {}, visibleKeys, openSection = null }) => {
  const currentCase = useCasevoStore((state) => state.currentCase)
  const caseId = currentCase?.id ?? null
  // The case's loaded deadlines (undefined while loading → no limitation chip
  // yet, so it never flickers in and out).
  const deadlines = useCasevoStore((state) => (caseId ? state.caseDeadlines[caseId] : undefined))
  // The parent rebuilds lockReasonByKey every render, so the filtered list is
  // keyed by its content (sig) to keep one array while nothing has changed.
  const fillable = requiredFieldsFor(currentCase, deadlines, lockReasonByKey, visibleKeys, openSection)
  const sig = fillable.map((f) => `${f.section}:${f.field}:${f.label}`).join('|')
  const [expanded, setExpanded] = useState(false)
  // What's on screen. Kept while the row folds away after the last field is
  // filled in, so it shrinks with its content rather than jumping.
  const [shown, setShown] = useState<{ caseId: string | null; sig: string; fields: MissingField[] }>({ caseId, sig, fields: fillable })
  if (shown.caseId !== caseId) {
    // Another case: show its state at once (no fold animation across cases).
    setShown({ caseId, sig, fields: fillable })
    setExpanded(false)
  } else if (fillable.length > 0 && shown.sig !== sig) {
    setShown({ caseId, sig, fields: fillable })
  }
  const leaving = fillable.length === 0 && shown.fields.length > 0
  useEffect(() => {
    if (!leaving) return undefined
    const t = window.setTimeout(() => setShown({ caseId, sig: '', fields: [] }), FOLD_MS)
    return () => window.clearTimeout(t)
  }, [leaving, caseId])
  if (!currentCase || shown.fields.length === 0) return null

  const list = shown.fields
  const { shown: visible, more } = visibleFields(list, expanded)
  const go = (f: MissingField): void => {
    requestFieldFocus({ section: f.section, field: f.field, dataField: f.dataField })
    onGo(f.section)
  }

  return (
    <div
      className={`grid transition-[grid-template-rows,opacity,margin-top] duration-300 ease-out motion-reduce:transition-none ${leaving ? 'grid-rows-[0fr] opacity-0 mt-0' : 'grid-rows-[1fr] opacity-100 mt-[12px]'}`}
      aria-hidden={leaving || undefined}
    >
      <div className="min-h-0 overflow-hidden">
        {/* ★ (Audit) The label sits in its own column; the chips wrap in theirs
            (so a second line of chips lines up with the first, not under
            "To fill in" — e.g. at 1024px). */}
        <section aria-label="Required fields" className="flex items-start gap-[10px] py-[3px] pl-[12px] border-l-2 border-casevo-warn-border">
          <span className="shrink-0 text-[12.5px] leading-[24px] text-casevo-text-soft">To fill in</span>
          <div className="flex flex-wrap items-center gap-[6px] min-w-0">
          {visible.map((f) => (
            <RailTooltip key={`${f.section}:${f.field}`} enabled={!leaving} placement="below" label={f.sectionLabel} className="inline-flex">
              <button
                type="button"
                className={CHIP_FIELD}
                aria-label={`${f.sectionLabel} · ${f.label}`}
                tabIndex={leaving ? -1 : undefined}
                onClick={() => go(f)}
              >
                {f.label}
                <KIcon name="arrow" size={11} className="opacity-[.45]" />
              </button>
            </RailTooltip>
          ))}
          {(more > 0 || (expanded && list.length > FOLD_AT)) && (
            <button
              type="button"
              className={CHIP_MORE}
              aria-expanded={expanded}
              tabIndex={leaving ? -1 : undefined}
              onClick={() => setExpanded((x) => !x)}
            >
              {expanded ? 'Show fewer' : `+${more} more`}
            </button>
          )}
          </div>
        </section>
      </div>
    </div>
  )
}
export default ValidationSummary
